/**
 * Intake Source React Query Hooks — J7-W7
 *
 * Wraps RorosIntakeService with TanStack Query for caching, invalidation,
 * and loading/error state management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RorosIntakeService,
  type ListIntakeSourcesParams,
  type CreateIntakeSourcePayload,
  type UpdateIntakeSourcePayload,
} from '@/services/roros/intake.service';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const intakeKeys = {
  sources: ['roros', 'intake', 'sources'] as const,
  source: (id: string) => ['roros', 'intake', 'sources', id] as const,
  sourceList: (params?: ListIntakeSourcesParams) =>
    ['roros', 'intake', 'sources', 'list', params] as const,
  scanRoutine: ['roros', 'intake', 'scan-routine'] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useRorosIntakeSources(params?: ListIntakeSourcesParams) {
  return useQuery({
    queryKey: intakeKeys.sourceList(params),
    queryFn: () => RorosIntakeService.listIntakeSources(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosIntakeSource(id: string) {
  return useQuery({
    queryKey: intakeKeys.source(id),
    queryFn: () => RorosIntakeService.getIntakeSource(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosIntakeScanRoutine() {
  return useQuery({
    queryKey: intakeKeys.scanRoutine,
    queryFn: () => RorosIntakeService.getIntakeScanRoutine(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    // B5: null response = no routine seeded (pre-migration-297 tenant). This is not
    // an error — the banner renders a "Not configured" state for data===null.
    // retry: false keeps the error state stable for the "service down" case.
    retry: false,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useRorosIntakeSourceCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateIntakeSourcePayload) =>
      RorosIntakeService.createIntakeSource(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: intakeKeys.sources });
    },
  });
}

export function useRorosIntakeSourceUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateIntakeSourcePayload }) =>
      RorosIntakeService.updateIntakeSource(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: intakeKeys.sources });
      qc.invalidateQueries({ queryKey: intakeKeys.source(id) });
    },
  });
}

export function useRorosIntakeSourceDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosIntakeService.disableIntakeSource(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: intakeKeys.sources });
    },
  });
}
